import { AfterViewInit, Directive, OnDestroy, OnInit } from '@angular/core';
import L, { DivIcon, Marker } from 'leaflet';
import { Subject } from 'rxjs';
import { DeviceService } from '../../../services/device.service';
import { MapIconService } from '../../../services/map-icon.service';
import { DeviceDto } from '../../../types/dtos/device.dto';
import { DeviceMapDto } from '../../../types/dtos/map.dto';
import { getMarkerStyle } from '../../utils/map/map-marker.util';

@Directive()
export abstract class AbstractMap implements AfterViewInit, OnDestroy, OnInit {
  protected _map!: L.Map;
  protected markerLayer: L.LayerGroup = L.layerGroup();
  protected resizeObserver?: ResizeObserver;

  protected mapDevices: DeviceMapDto[] = [];
  protected devices: DeviceMapDto[] = [];

  protected destroy$: Subject<void> = new Subject<void>();

  protected constructor(
    protected readonly _deviceService: DeviceService,
    protected readonly _mapIconService: MapIconService,
  ) {
  }

  public abstract ngOnInit(): void;

  public abstract ngAfterViewInit(): void | Promise<void>;

  protected updateDevicesToMapDevices(devices: DeviceDto[]): void {
    this.mapDevices = [];

    devices = devices.filter((device: DeviceDto) => !!device.position);

    const positionGroups: Map<string, DeviceDto[]> = this.getDevicePositionGroups(devices);

    devices.forEach((device: DeviceDto) => {
      const positionKey: string = this.getPositionKey(device);
      const samePositionDevices: DeviceDto[] = positionGroups.get(positionKey) ?? [device];

      const samePositionIndex: number = samePositionDevices.findIndex((deviceItem: DeviceDto) =>
        deviceItem.id === device.id,
      );

      const markerPosition: number[] = this.getSpreadMarkerPosition(
        device,
        Math.max(samePositionIndex, 0),
        samePositionDevices.length,
      );

      if (!markerPosition) return;
      if (!markerPosition[0] || !markerPosition[1]) return;

      const { color, iconCode } = getMarkerStyle(device);

      if (!device.position) return;

      this.mapDevices.push({
        ...device,
        iconColor: color,
        iconCode,
        position: {
          ...device.position,
          latitude: markerPosition[0],
          longitude: markerPosition[1],
        }
      });
    });

  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this._map?.remove();

    this.destroy$.next();
    this.destroy$.complete();
  }

  protected addMarkers(): void {
    if (!this._map) return;
    this.markerLayer.clearLayers();

    const iconSize: number = this.getMarkerIconSize();

    this.mapDevices.forEach((device: DeviceMapDto) => {
      if (!device.position) return;

      const icon: DivIcon = L.divIcon({
        className: '',
        html: this._mapIconService.renderIcon(device.iconColor, device.iconCode, iconSize),
      });

      const marker: Marker = L.marker([device.position.latitude, device.position.longitude], {
        icon,
        opacity: 1,
      })
        .addTo(this.markerLayer);

      this.updateMarker(marker, device);
    });
  }

  protected updateMarker(marker: Marker, device: DeviceMapDto) {
  }

  protected createDefaultTileLayer(): L.TileLayer {
    return L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    });
  }

  protected getDeviceValue(device: DeviceDto): string {
    return device.last_measurement && device.last_measurement['METER_VALUE_1']
           ? `${ (Number(device.last_measurement['METER_VALUE_1']) / 1000).toFixed(2) } m³`
           : '-';
  }

  protected _fitMapToDevices(): void {
    if (!this._map) {
      return;
    }

    const markerPositions: L.LatLngExpression[] = this.devices
      .filter((device: DeviceDto) => !!device.position)
      .map((device: DeviceDto) => [
        device.position!.latitude,
        device.position!.longitude,
      ]);

    if (!markerPositions.length) {
      this._map.setView(this._deviceService.getCenter(), 12);
      return;
    }

    if (markerPositions.length === 1) {
      this._map.setView(markerPositions[0], 15);
      return;
    }

    const bounds: L.LatLngBounds = L.latLngBounds(markerPositions);

    this._map.fitBounds(bounds, {
      padding: [
        50,
        50,
      ],
      maxZoom: 16,
      animate: true,
    });
  }

  protected getMarkerIconSize(): number {
    const zoom: number = this._map.getZoom();

    return Math.min(24, Math.max(12, zoom * 1.2));
  }

  protected getDevicePositionGroups(devices: DeviceDto[]): Map<string, DeviceDto[]> {
    const positionGroups: Map<string, DeviceDto[]> = new Map<string, DeviceDto[]>();

    devices
      .filter((device: DeviceDto) => !!device.position)
      .forEach((device: DeviceDto) => {
      const positionKey: string = this.getPositionKey(device);
      const groupedDevices: DeviceDto[] = positionGroups.get(positionKey) ?? [];

      groupedDevices.push(device);
      positionGroups.set(positionKey, groupedDevices);
    });

    return positionGroups;
  }

  protected getPositionKey(device: DeviceDto): string {
    if (!device.position) return '';

    return `${ device.position.latitude.toFixed(6) }:${ device.position.longitude.toFixed(6) }`;
  }

  protected getSpreadMarkerPosition(
    device: DeviceDto,
    index: number,
    total: number,
  ): number[] {
    if (!device.position || total <= 1) {
      return [
        device.position?.latitude ?? 0,
        device.position?.longitude ?? 0,
      ];
    }

    const baseLatitude: number = device.position.latitude;
    const baseLongitude: number = device.position.longitude;

    const radiusInMeters: number = 3;
    const angle: number = (2 * Math.PI * index) / total;

    const latitudeOffset: number =
            (Math.sin(angle) * radiusInMeters) / 111_320;

    const longitudeOffset: number =
            (Math.cos(angle) * radiusInMeters)
            / (111_320 * Math.cos(baseLatitude * Math.PI / 180));

    return [
      baseLatitude + latitudeOffset,
      baseLongitude + longitudeOffset,
    ];
  }
}
